import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { COUNTRIES } from "@/lib/countries";
import { Wordmark } from "@/components/Brand";
import { Search, Globe, Check, X, ArrowRight, ChevronLeft, Landmark, ShieldCheck } from "lucide-react";

export default function CountrySelector({ current, currentRegion, onComplete, onClose }) {
  const [view, setView] = useState("country"); // country | region
  const [pending, setPending] = useState(null); // country awaiting a region
  const [query, setQuery] = useState("");

  const list = COUNTRIES.filter((c) => c.name.toLowerCase().includes(query.trim().toLowerCase()));

  const pickCountry = (c) => {
    if (c.status === "soon") return;
    if (c.subSelect) {
      setPending(c);
      setQuery("");
      setView("region");
    } else {
      onComplete(c.code, null);
    }
  };
  const pickRegion = (r) => { if (r.status !== "soon") onComplete(pending.code, r.code); };
  const backToCountry = () => { setPending(null); setView("country"); };

  return (
    <motion.div
      data-testid="country-selector"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.4 } }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-[100] grain bg-[#1B1C1A] text-white overflow-y-auto"
    >
      <div className="absolute inset-0 techgrid-dark opacity-60" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#2BBFB3]/10 blur-3xl" />
      <Globe className="pointer-events-none absolute -right-24 -bottom-24 text-white/[0.03]" size={520} strokeWidth={0.4} />

      <div className="relative z-10 min-h-full flex flex-col">
        {/* top bar */}
        <div className="flex items-center justify-between px-6 sm:px-10 py-6">
          <Wordmark light />
          {onClose && view === "country" && (
            <button
              data-testid="country-selector-close"
              onClick={onClose}
              className="grid place-items-center w-10 h-10 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <AnimatePresence mode="wait">
          {view === "country" ? (
            <motion.div
              key="country"
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.35 }}
              className="flex-1 flex flex-col"
            >
              <Header
                overline="Built around real regulation"
                title={<>Where do you <span className="text-[#2BBFB3]">teach?</span></>}
                sub="DriveAssure is built around the legislation that governs driving instruction in each market. Select your country to continue — we'll remember it next time."
              />
              <div className="px-6 sm:px-10 max-w-md w-full">
                <SearchBox query={query} setQuery={setQuery} placeholder="Search countries…" />
              </div>

              <div className="px-6 sm:px-10 py-8 flex-1">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl">
                  {list.map((c, i) => {
                    const soon = c.status === "soon";
                    const selected = current?.code === c.code;
                    return (
                      <motion.button
                        key={c.code}
                        data-testid={`country-card-${c.code}`}
                        disabled={soon}
                        onClick={() => pickCountry(c)}
                        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.08 + i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                        className={`group relative overflow-hidden rounded-2xl border p-5 text-left transition-[transform,border-color,background-color] ${
                          soon
                            ? "border-white/10 bg-white/[0.02] cursor-not-allowed"
                            : selected
                            ? "border-[#2BBFB3] bg-[#2BBFB3]/[0.08]"
                            : "border-white/12 bg-white/[0.04] hover:border-[#2BBFB3]/60 hover:bg-white/[0.07] hover:-translate-y-1"
                        }`}
                      >
                        {soon && <Sash />}
                        <div className="flex items-center gap-4">
                          <span className={`block w-16 h-11 rounded-lg overflow-hidden ring-1 ring-white/15 shrink-0 ${soon ? "opacity-45 grayscale" : ""}`}>
                            <c.Flag />
                          </span>
                          <div className="min-w-0">
                            <p className={`font-display font-bold text-lg leading-tight ${soon ? "text-white/50" : "text-white"}`}>{c.name}</p>
                            <p className={`mt-1 flex items-start gap-1.5 text-[12px] leading-snug ${soon ? "text-[#F5A623]/80" : "text-white/55"}`}>
                              <Landmark size={12} className={`mt-0.5 shrink-0 ${soon ? "text-[#F5A623]/70" : "text-[#2BBFB3]"}`} />
                              {c.regulator}
                            </p>
                          </div>
                        </div>

                        <span className="mt-4 flex items-center justify-between">
                          {c.subSelect ? (
                            <span className="text-[11px] text-[#2BBFB3] font-medium">Choose your {c.subLabel} next →</span>
                          ) : (
                            <span className="text-[11px] uppercase tracking-widest text-white/35">{soon ? "Coming soon" : c.code}</span>
                          )}
                          {!soon && (selected && !c.subSelect ? (
                            <span className="grid place-items-center w-7 h-7 rounded-full bg-[#2BBFB3] text-[#0f1211]"><Check size={15} strokeWidth={3} /></span>
                          ) : (
                            <ArrowRight size={16} className="text-white/30 transition-transform group-hover:translate-x-1 group-hover:text-[#2BBFB3]" />
                          ))}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
                {list.length === 0 && <p className="text-white/50 text-sm">No countries match “{query}”.</p>}
                <p className="mt-10 text-xs text-white/35">You can change your region anytime from the flag in the navigation.</p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="region"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.35 }}
              className="flex-1 flex flex-col"
            >
              <div className="px-6 sm:px-10">
                <button
                  data-testid="region-back"
                  onClick={backToCountry}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-white/60 hover:text-white transition-colors"
                >
                  <ChevronLeft size={16} /> Back to countries
                </button>
              </div>
              <Header
                overline={`${pending?.name} · ${pending?.regulator}`}
                title={<>Select your <span className="text-[#2BBFB3]">{pending?.subLabel}</span></>}
                sub="Driving instruction is regulated locally. Choose where you operate so we can show the rules and requirements that apply to you."
              />
              <div className="px-6 sm:px-10 py-8 flex-1">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl">
                  {pending?.subRegions.map((r, i) => {
                    const soon = r.status === "soon";
                    const selected = current?.code === pending.code && currentRegion?.code === r.code;
                    return (
                      <motion.button
                        key={r.code}
                        data-testid={`region-card-${r.code}`}
                        disabled={soon}
                        onClick={() => pickRegion(r)}
                        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.06 + Math.min(i, 12) * 0.03, ease: [0.22, 1, 0.36, 1] }}
                        className={`group relative overflow-hidden rounded-2xl border p-5 text-left transition-[transform,border-color,background-color] ${
                          soon
                            ? "border-white/10 bg-white/[0.02] cursor-not-allowed"
                            : r.primary
                            ? "border-[#2BBFB3] bg-[#2BBFB3]/[0.08]"
                            : selected
                            ? "border-[#2BBFB3] bg-[#2BBFB3]/[0.06]"
                            : "border-white/12 bg-white/[0.04] hover:border-[#2BBFB3]/60 hover:bg-white/[0.07] hover:-translate-y-1"
                        }`}
                      >
                        {soon && <Sash />}
                        <div className="flex items-center gap-3">
                          <span className={`grid place-items-center w-12 h-12 rounded-xl font-display font-extrabold text-sm shrink-0 ${soon ? "bg-white/[0.03] text-white/35" : "bg-white/5 text-[#2BBFB3]"}`}>
                            {r.code}
                          </span>
                          <div className="min-w-0">
                            <p className={`font-display font-bold text-[15px] leading-tight ${soon ? "text-white/45" : "text-white"}`}>{r.name}</p>
                            <p className={`mt-1 flex items-start gap-1.5 text-[11px] leading-snug ${soon ? "text-white/30" : "text-white/50"}`}>
                              <Landmark size={11} className={`mt-0.5 shrink-0 ${soon ? "text-white/25" : "text-white/40"}`} />
                              {r.authority}
                            </p>
                          </div>
                        </div>
                        <span className="mt-3 flex items-center justify-between">
                          {soon ? (
                            <span className="text-[11px] uppercase tracking-widest text-[#F5A623]/80 font-semibold">Coming soon</span>
                          ) : r.primary ? (
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#F5A623]">
                              <ShieldCheck size={13} /> {r.note}
                            </span>
                          ) : <span />}
                          {!soon && <ArrowRight size={16} className="text-white/30 transition-transform group-hover:translate-x-1 group-hover:text-[#2BBFB3]" />}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function Header({ overline, title, sub }) {
  return (
    <div className="px-6 sm:px-10 pt-4 pb-6 max-w-3xl">
      <motion.span
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 overline text-[#2BBFB3]"
      >
        <Globe size={13} /> {overline}
      </motion.span>
      <motion.h1
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}
        className="mt-6 font-display font-extrabold tracking-tight text-4xl sm:text-6xl leading-[0.95]"
      >
        {title}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="mt-5 max-w-xl text-lg text-white/60 leading-relaxed"
      >
        {sub}
      </motion.p>
    </div>
  );
}

function SearchBox({ query, setQuery, placeholder }) {
  return (
    <div className="relative">
      <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
      <input
        data-testid="country-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-full border border-white/15 bg-white/5 pl-11 pr-4 py-3.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-[#2BBFB3] focus:ring-2 focus:ring-[#2BBFB3]/30 transition-colors"
      />
    </div>
  );
}

function Sash() {
  return (
    <span className="pointer-events-none absolute top-0 right-0 w-28 h-28 overflow-hidden">
      <span className="absolute right-[-34px] top-[18px] w-[140px] rotate-45 bg-[#F5A623] py-1 text-center text-[10px] font-extrabold uppercase tracking-wide text-[#2B2B2B]">
        Coming soon
      </span>
    </span>
  );
}
